/* eslint-disable complexity */
import {
  AbsatzPlan,
  AbsatzProduktivitaetEinstellungen,
  AbsatzVorgabe,
  UmsatzPlan,
  UmsatzProduktivitaetEinstellungen,
} from '@/domain/controlling/entities';
import {
  formatNumber,
  getAbsatzProduktivitaetByMptOrStueck,
} from '../../utils';
import { Anzeige } from '@/domain/controlling/entities/tagebuchAnzeige';
import { Planwert } from '@/domain/controlling/entities/planwert';

function isUmsatzPlanwertValid(umsatzPlanwert: UmsatzPlan | null) {
  if (!umsatzPlanwert || !umsatzPlanwert.umsatz) {
    return false;
  }
  if (!umsatzPlanwert.umsatz.wert || umsatzPlanwert.umsatz.wert.amount <= 0) {
    return false;
  }
  if (
    !umsatzPlanwert.umsatz.produktivitaet ||
    umsatzPlanwert.umsatz.produktivitaet.amount <= 0
  ) {
    return false;
  }
  return true;
}

function isAbsatzPlanwertValid(
  absatzVorgabe: AbsatzVorgabe,
  absatzPlanwert: AbsatzPlan | null,
) {
  if (!absatzPlanwert || !absatzPlanwert.absatz) {
    return false;
  }
  if (!absatzPlanwert.absatz.wert || absatzPlanwert.absatz.wert <= 0) {
    return false;
  }
  if (!absatzPlanwert.absatz.produktivitaet) {
    return false;
  }
  if (
    (absatzVorgabe === AbsatzVorgabe.Mpt &&
      absatzPlanwert.absatz.produktivitaet.mpt <= 0) ||
    (absatzVorgabe === AbsatzVorgabe.Stueck &&
      absatzPlanwert.absatz.produktivitaet.stueck <= 0)
  ) {
    return false;
  }
  return true;
}

export function isPlanwertValid(
  umsatzplanungAnzeige: Anzeige,
  absatzVorgabe: AbsatzVorgabe,
  planwert: Planwert,
) {
  if (umsatzplanungAnzeige === 'UMSATZ_PRODUKTIVITAET') {
    return isUmsatzPlanwertValid(planwert.umsatzPlan);
  }
  if (umsatzplanungAnzeige === 'ABSATZ_PRODUKTIVITAET') {
    return isAbsatzPlanwertValid(absatzVorgabe, planwert.absatzPlan);
  }
  if (umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET') {
    return (
      isUmsatzPlanwertValid(planwert.umsatzPlan) &&
      isAbsatzPlanwertValid(absatzVorgabe, planwert.absatzPlan)
    );
  }
}

export function getDefaultAbsatzPlanwert(absatzPlanwert?: AbsatzPlan) {
  return absatzPlanwert && absatzPlanwert.absatz && absatzPlanwert.absatz.wert
    ? formatNumber(absatzPlanwert.absatz.wert.toString(), true)
    : '';
}

export function getDefaultAbsatzPrognose(absatzPlanwert?: AbsatzPlan) {
  return absatzPlanwert && absatzPlanwert.prognose
    ? absatzPlanwert.prognose
    : undefined;
}

export function getDefaultAbsatzProduktivitaet(
  absatzproduktivitaetEinstellungen: AbsatzProduktivitaetEinstellungen,
  absatzPlanwert?: AbsatzPlan,
) {
  return absatzPlanwert &&
    absatzPlanwert.absatz &&
    absatzPlanwert.absatz.produktivitaet
    ? getAbsatzProduktivitaetByMptOrStueck(
        absatzPlanwert.absatz.produktivitaet,
        absatzproduktivitaetEinstellungen,
        true,
      )
    : formatNumber(
        absatzproduktivitaetEinstellungen.zielProduktivitaet.toString(),
        true,
      );
}

export function getDefaultAbsatzminuten(absatzPlanwert?: AbsatzPlan) {
  return absatzPlanwert &&
    absatzPlanwert.absatz &&
    absatzPlanwert.absatz.stunden
    ? absatzPlanwert.absatz.stunden
    : undefined;
}

export function getDefaultUmsatzPlanwert(umsatzPlanwert?: UmsatzPlan) {
  return umsatzPlanwert && umsatzPlanwert.umsatz && umsatzPlanwert.umsatz.wert
    ? formatNumber(umsatzPlanwert.umsatz.wert.amount.toString(), false)
    : '';
}

export function getDefaultUmsatzProduktivitaet(
  umsatzProduktivitaetEinstellungen: UmsatzProduktivitaetEinstellungen,
  umsatzPlanwert?: UmsatzPlan,
) {
  return umsatzPlanwert &&
    umsatzPlanwert.umsatz &&
    umsatzPlanwert.umsatz.produktivitaet
    ? formatNumber(
        umsatzPlanwert.umsatz.produktivitaet.amount.toString(),
        false,
      )
    : formatNumber(
        umsatzProduktivitaetEinstellungen.zielProduktivitaet.toString(),
        false,
      );
}

export function getDefaultUmsatzPrognose(umsatzPlanwert?: UmsatzPlan) {
  return umsatzPlanwert && umsatzPlanwert.prognose
    ? umsatzPlanwert.prognose
    : undefined;
}

export function getDefaultUmsatzZielminuten(umsatzPlanwert?: UmsatzPlan) {
  return umsatzPlanwert &&
    umsatzPlanwert.umsatz &&
    umsatzPlanwert.umsatz.stunden
    ? umsatzPlanwert.umsatz.stunden
    : undefined;
}
